import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import DropdownAdapter from './DropdownAdapter';
import { tabsOptionShape } from './shapes';
import Tabs from './Tabs';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;
const MAX_NUM_MOBILE_TABS = 2;

const TabsContainer = ({ ariaLabel, ariaLabelledBy, centered, initialSelectedOptionId, onTabSelected, options }) => {
  const initialActiveIndex = initialSelectedOptionId
    ? options.findIndex(option => option.id === initialSelectedOptionId)
    : 0;

  const [activeTabIndex, setActiveTabIndex] = useState(initialActiveIndex);
  const [focusableTabIndex, setFocusableTabIndex] = useState(initialActiveIndex);
  const tabRefs = useRef([]);

  useEffect(() => {
    const { current: tabEls = [] } = tabRefs;

    const shouldChangeFocus = tabEls.some((tabEl, tabIndex) => {
      const isFocusable = focusableTabIndex === tabIndex;
      const isFocused = document.activeElement === tabEl;

      return !isFocusable && isFocused;
    });

    if (shouldChangeFocus) {
      tabEls[focusableTabIndex].focus();
    }
  }, [focusableTabIndex]);

  const hasDropdownWhenMobile = options.length > MAX_NUM_MOBILE_TABS;
  const numTabs = (options ?? []).length;

  const handleDropdownSelect = optionId => {
    const selectedOptionIndex = options.findIndex(option => option.id === optionId);
    if (selectedOptionIndex === -1) {
      return;
    }

    setActiveTabIndex(selectedOptionIndex);
    setFocusableTabIndex(selectedOptionIndex);

    onTabSelected?.(options[selectedOptionIndex]);
  };

  const handleTabBlur = () => {
    setTimeout(() => {
      const { current: tabEls } = tabRefs;

      const hasFocusLeftTabList = !Array.from(tabEls).some(tabEl => {
        return tabEl === document.activeElement;
      });

      if (hasFocusLeftTabList) {
        setFocusableTabIndex(activeTabIndex);
      }
    }, 0);
  };

  const handleTabClick = tabIndex => {
    const selectedOption = options[tabIndex];

    setActiveTabIndex(tabIndex);
    setFocusableTabIndex(tabIndex);

    onTabSelected?.(selectedOption);
  };

  const handleTabKeyDown = event => {
    const keyCode = event.which;

    switch (keyCode) {
      case KEY_CODE_ARROW_LEFT: {
        let newIndex = focusableTabIndex - 1;

        if (newIndex < 0) {
          newIndex = numTabs - 1;
        }

        setFocusableTabIndex(newIndex);
        break;
      }
      case KEY_CODE_ARROW_RIGHT: {
        let newIndex = focusableTabIndex + 1;

        if (newIndex > numTabs - 1) {
          newIndex = 0;
        }

        setFocusableTabIndex(newIndex);
        break;
      }
      default:
        break;
    }
  };

  return (
    <>
      <Tabs
        activeTabIndex={activeTabIndex}
        ariaLabel={ariaLabel}
        ariaLabelledBy={ariaLabelledBy}
        centered={centered}
        focusableTabIndex={focusableTabIndex}
        handleTabBlur={handleTabBlur}
        handleTabClick={handleTabClick}
        handleTabKeyDown={handleTabKeyDown}
        isHiddenWhenMobile={hasDropdownWhenMobile}
        options={options}
        tabRefs={tabRefs}
      />
      {hasDropdownWhenMobile && (
        <>
          <DropdownAdapter
            activeOptionIndex={activeTabIndex}
            centered={centered}
            handleSelect={handleDropdownSelect}
            options={options}
          />
        </>
      )}
    </>
  );
};

TabsContainer.defaultProps = {
  centered: false,
  onTabSelected: () => null
};

TabsContainer.propTypes = {
  ariaLabel: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  centered: PropTypes.bool,
  initialSelectedOptionId: PropTypes.string,
  onTabSelected: PropTypes.func,
  options: PropTypes.arrayOf(tabsOptionShape).isRequired
};

export default TabsContainer;
